import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { EVENT_DATA_EXAMPLES } from './constants/event-data-examples.const';
import { EventsFlowActionNodeTypes } from '../models/events-flow-action-node-types';
import { Observable, Subscription, take } from 'rxjs';
import { INodeViewModel } from '../../../core/visual/work-flow-editor/models/i-node-view-model';
import { hasEventsFlowEventNodeTypes } from '../models/events-flow-event-node-types';
import { FormControl, FormGroup, UntypedFormArray, UntypedFormControl, UntypedFormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import {
  EditWorkflowEventDialogComponent,
  EditWorkflowEventDialogData,
} from './edit-workflow-event-dialog-component/edit-workflow-event-dialog-component.component';
import { WORKFLOW_EVENTS } from '../palette/workflow-palette-events/workflow-events-constant';
import { LookupModel } from '../../../api/models';
import { ActionsNotes } from '../models/action-notes.interface';
import { EventsFlowDataFormService } from '../../services/events-flow-data-form.service';
import { NgIf, NgFor, NgSwitch, NgSwitchCase } from '@angular/common';
import { FlexModule } from '@angular/flex-layout';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTextInputComponent } from '../../../editors/mat-text-input/mat-text-input.component';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatAccordion, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from '@angular/material/expansion';
import { NgxJsonViewerModule } from 'ngx-json-viewer';
import { EventsFlowUrlSettingsComponent } from '../forms/url-settings/url-settings.component';
import { EventsFlowHhaCaregiverNotesSettingsComponent } from '../forms/hha-caregiver-notes-settings/hha-caregiver-notes-settings.component';
import { EventsFlowHhaPatientNotesSettingsComponent } from '../forms/hha-patient-notes-settings/hha-patient-notes-settings.component';
import { TranscriptionSettingsComponent } from '../forms/transcription-settings/transcription-settings.component';
import { EventFiltersComponent } from '../forms/event-filters/event-filters.component';

@Component({
    selector: 'workflow-node-parameters',
    templateUrl: './workflow-node-parameters.component.html',
    styleUrls: ['./workflow-node-parameters.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        NgIf,
        FlexModule,
        MatIconButton,
        MatIcon,
        MatTextInputComponent,
        FormsModule,
        ReactiveFormsModule,
        NgFor,
        MatSlideToggle,
        MatAccordion,
        MatExpansionPanel,
        MatExpansionPanelHeader,
        MatExpansionPanelTitle,
        NgxJsonViewerModule,
        NgSwitch,
        NgSwitchCase,
        EventsFlowUrlSettingsComponent,
        EventsFlowHhaCaregiverNotesSettingsComponent,
        EventsFlowHhaPatientNotesSettingsComponent,
        TranscriptionSettingsComponent,
        EventFiltersComponent,
    ],
})
export class WorkflowNodeParametersComponent implements OnInit, OnDestroy {
  @Input({ required: true })
  nodes$!: Observable<INodeViewModel<any>[]>;

  @Input({ required: true })
  actionNotes!: ActionsNotes;

  @Output() parameterWindowClosed = new EventEmitter<string>();
  @Output() nodeUpdated = new EventEmitter<INodeViewModel<any>>();

  public nodes: INodeViewModel<any>[] = [];

  public readonly EventsFlowActionNodeTypes = EventsFlowActionNodeTypes;

  private readonly _subscriptions = new Subscription();

  constructor(
    private readonly _cdr: ChangeDetectorRef,
    private readonly _dialog: MatDialog,
    private readonly _eventsFlowDataFormService: EventsFlowDataFormService,
  ) {}

  get eventFlowNameControl(): FormControl {
    return this._eventsFlowDataFormService.eventsFlowForm?.get('eventFlowName') as FormControl;
  }

  public ngOnInit(): void {
    this._subscriptions.add(this.subscribeNodes());
  }

  public ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  public getActionItemSettingsForm(actionId: string): FormGroup {
    return this._eventsFlowDataFormService.getEventFlowActionItemSettingsForm(actionId);
  }

  public isNodeEventType(nodeType: string): boolean {
    return hasEventsFlowEventNodeTypes(nodeType);
  }

  public getEventDataExample(eventType: string): any {
    return EVENT_DATA_EXAMPLES[eventType];
  }

  public onClose(): void {
    this.parameterWindowClosed.emit();
  }

  public onOpenEdit(node: INodeViewModel<any>): void {
    const selectOptions: LookupModel[] = WORKFLOW_EVENTS.map(event => ({
      id: event.type,
      text: event.name,
    }));

    const dialogRef = this._dialog.open(EditWorkflowEventDialogComponent, {
      width: '400px',
      data: {
        selectOptions,
        node,
      } satisfies EditWorkflowEventDialogData,
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((nodeType: string) => {
        if (nodeType) {
          const newEvent = WORKFLOW_EVENTS.find(event => event.type === nodeType);
          const newNode = { ...node, ...newEvent } satisfies INodeViewModel<any>;

          this.nodeUpdated.next(newNode);
        }
      });
  }

  private subscribeNodes(): Subscription {
    return this.nodes$.subscribe(data => {
      this.nodes = data || [];

      if (!data.length) {
        setTimeout(() => {
          this._cdr.detectChanges();
        });
      }
    });
  }
}
